import { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Copy,
  ExternalLink,
  GitBranch,
  Github,
  Loader2,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import {
  useSpacePlugin,
  useCheckPluginUpdates,
  useSyncPlugin,
  useUninstallPlugin,
  usePluginDetailEvents,
  useAwaitPluginCompletion,
  useSetAutoUpdate,
} from '../../hooks/usePlugins';
import { useSpaceSkills } from '../../hooks/useSkills';
import { useSpaceAgents } from '../../hooks/useAgents';
import { useSpaceCredentials } from '../../hooks/useIdentityCredentials';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import {
  Badge,
  Button,
  ConfirmModal,
  ControlledTabs,
  Switch,
  Table,
  getStatusVariant,
  type BadgeVariant,
} from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import BackLink from '../../components/navigation/BackLink';
import type { Plugin, PluginStatus } from '../../types';
import SwitchRefPopover from './SwitchRefPopover';
import { ResourceCard, ResourceIcon, type ResourceKind } from './ResourceCard';

function pluginStatusVariant(status: PluginStatus): BadgeVariant {
  if (status === 'syncing') return 'info';
  return getStatusVariant(status);
}

function HostIcon({ host }: { host: Plugin['source']['host'] }) {
  if (host === 'github') return <Github className="h-4 w-4" />;
  return <GitBranch className="h-4 w-4" />;
}

export default function PluginDetail() {
  const { pluginId } = useParams<{ pluginId: string }>();
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();

  const { data: plugin, isLoading, error } = useSpacePlugin(pluginId, {
    expand: ['skills', 'agents'],
  });
  // The plugin response carries IDs in `resources.{skills,agents}`. We
  // look them up against the space's full lists to render names and
  // resourceKeys — `resourcesExpanded` is unreliable today.
  const { data: spaceSkillsData } = useSpaceSkills();
  const { data: spaceAgentsData } = useSpaceAgents();
  const { data: credsData } = useSpaceCredentials(spaceId);
  const checkUpdates = useCheckPluginUpdates();
  const sync = useSyncPlugin();
  const uninstall = useUninstallPlugin();
  const setAutoUpdate = useSetAutoUpdate();

  usePluginDetailEvents(pluginId);
  const completion = useAwaitPluginCompletion(
    plugin?.status === 'syncing' ? pluginId : undefined,
  );

  const [activeTab, setActiveTab] = useState('resources');
  const [uninstallOpen, setUninstallOpen] = useState(false);
  const [refPopoverOpen, setRefPopoverOpen] = useState(false);

  // Auto check-for-updates once per plugin so the page lands with a current
  // view of `upstream.updateAvailable`. Must sit before any early return so
  // the hook order stays stable across loading and loaded renders.
  const autoCheckedRef = useRef<string | null>(null);
  const checkUpdatesMutate = checkUpdates.mutate;
  useEffect(() => {
    if (!plugin) return;
    if (autoCheckedRef.current === plugin.pluginId) return;
    if (plugin.source.refType === 'sha') return;
    if (plugin.status === 'syncing' || plugin.status === 'failed') return;
    autoCheckedRef.current = plugin.pluginId;
    checkUpdatesMutate(plugin.pluginId);
  }, [plugin, checkUpdatesMutate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !plugin) {
    return (
      <div className="text-center py-12">
        <p className="text-status-error-fg">Failed to load plugin</p>
        <Link
          to={routes.console.spacePlugins(spaceId)}
          className="text-primary-600 hover:text-primary-500 mt-2 inline-block"
        >
          Back to plugins
        </Link>
      </div>
    );
  }

  // Source of truth: the ID arrays on `resources`. Join against the space
  // skill/agent lists to fill in names + resourceKey, falling back to the
  // bare ID if the entity isn't in the cached list yet.
  const skillIds = plugin.resources.skills ?? [];
  const agentIds = plugin.resources.agents ?? [];
  const skillsById = new Map(
    (spaceSkillsData?.items ?? []).map((s) => [s.skillId, s]),
  );
  const agentsById = new Map(
    (spaceAgentsData?.items ?? []).map((a) => [a.agentId, a]),
  );
  const skills = skillIds.map((id) => {
    const s = skillsById.get(id);
    return {
      skillId: id,
      name: s?.name ?? id,
      description: s?.description,
      resourceKey: s?.provenance?.resourceKey,
    };
  });
  const agents = agentIds.map((id) => {
    const a = agentsById.get(id);
    return {
      agentId: id,
      name: a?.name ?? id,
      description: a?.description,
      resourceKey: a?.provenance?.resourceKey,
    };
  });
  const mcpServers = plugin.resources.mcpServersReported ?? [];

  const updateAvailable = !!plugin.upstream?.updateAvailable;
  const refMissing = !!plugin.upstream?.refMissing;
  const isSyncing = plugin.status === 'syncing';
  const isSha = plugin.source.refType === 'sha';
  const hasUpstream = !!plugin.upstream;

  // Single update-status control derived from sync + check state.
  type UpdateActionKind =
    | 'checking'
    | 'syncing'
    | 'update-available'
    | 'up-to-date'
    | 'ref-missing'
    | 'pinned';

  let updateAction: {
    kind: UpdateActionKind;
    label: string;
    icon: 'spinner' | 'refresh';
    onClick?: () => void;
    variant?: 'outline';
    title?: string;
    disabled: boolean;
  };

  if (isSyncing || sync.isPending) {
    updateAction = {
      kind: 'syncing',
      label: 'Syncing…',
      icon: 'spinner',
      variant: 'outline',
      disabled: true,
    };
  } else if (isSha) {
    updateAction = {
      kind: 'pinned',
      label: 'Pinned to SHA',
      icon: 'refresh',
      variant: 'outline',
      title: 'Cannot sync a SHA-pinned plugin',
      disabled: true,
    };
  } else if (checkUpdates.isPending || !hasUpstream) {
    updateAction = {
      kind: 'checking',
      label: 'Checking for updates',
      icon: 'spinner',
      variant: 'outline',
      disabled: true,
    };
  } else if (refMissing) {
    updateAction = {
      kind: 'ref-missing',
      label: 'Ref missing',
      icon: 'refresh',
      variant: 'outline',
      title: `The tracked ref (${plugin.source.ref}) no longer exists upstream`,
      disabled: true,
    };
  } else if (updateAvailable) {
    updateAction = {
      kind: 'update-available',
      label: 'Update',
      icon: 'refresh',
      onClick: handleSync,
      disabled: false,
    };
  } else {
    updateAction = {
      kind: 'up-to-date',
      label: 'Up to date',
      icon: 'refresh',
      variant: 'outline',
      onClick: handleCheckUpdates,
      title: 'Click to re-check',
      disabled: false,
    };
  }

  async function handleCheckUpdates() {
    try {
      const upstream = await checkUpdates.mutateAsync(plugin!.pluginId);
      if (upstream.refMissing) {
        showError(
          `The tracked ref (${plugin!.source.ref}) no longer exists upstream.`,
        );
      } else if (upstream.updateAvailable) {
        success('Update available — click Sync to apply.');
      } else {
        success('Plugin is up to date.');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to check for updates');
    }
  }

  async function handleSync() {
    try {
      await sync.mutateAsync(plugin!.pluginId);
      success('Sync started');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to start sync');
    }
  }

  async function handleAutoUpdateChange(checked: boolean) {
    try {
      await setAutoUpdate.mutateAsync({
        pluginId: plugin!.pluginId,
        autoUpdate: checked,
      });
      // Passive flag — flipping it doesn't sync now. Word the toast so the
      // user knows manual "Update" remains the way to apply changes immediately.
      success(
        checked
          ? 'Auto-update on — applied on the next scheduled check.'
          : 'Auto-update off.',
      );
    } catch (err) {
      showError(
        err instanceof Error ? err.message : 'Failed to update auto-update flag',
      );
    }
  }

  async function handleUninstall() {
    try {
      await uninstall.mutateAsync(plugin!.pluginId);
      success('Plugin uninstalled');
      navigate(routes.console.spacePlugins(spaceId));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to uninstall plugin');
    }
  }

  const tabs = [
    {
      id: 'resources',
      label: `Resources (${skills.length + agents.length})`,
    },
    { id: 'skipped', label: `Skipped (${plugin.skipped.length})` },
    { id: 'mcp', label: `MCP servers (${mcpServers.length})` },
    { id: 'source', label: 'Source' },
  ];

  const matchedCredential = plugin.source.connectionId
    ? credsData?.credentials.find(
        (c) =>
          c.credId === plugin.source.connectionId ||
          c.providerInstanceId === plugin.source.connectionId,
      )
    : undefined;

  const installedAtLabel = new Date(plugin.installedAt).toLocaleString();
  const lastSyncedLabel = plugin.lastSyncedAt
    ? new Date(plugin.lastSyncedAt).toLocaleString()
    : null;

  return (
    <div className="space-y-6">
      <div>
        <BackLink to={routes.console.spacePlugins(spaceId)}>Plugins</BackLink>
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-foreground truncate">
                {plugin.displayName ?? plugin.name}
              </h1>
            {plugin.version && (
              <span className="text-sm text-muted-foreground">v{plugin.version}</span>
            )}
            <Badge variant={pluginStatusVariant(plugin.status)}>{plugin.status}</Badge>
            {updateAvailable && <Badge variant="info">Update available</Badge>}
            {refMissing && <Badge variant="warning">Ref missing upstream</Badge>}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {plugin.description || 'No description'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <a
              href={plugin.source.repoUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
            >
              <HostIcon host={plugin.source.host} />
              <span className="font-mono">
                {plugin.source.repoUrl.replace(/^https?:\/\//, '').replace(/\.git$/, '')}
              </span>
              <ExternalLink className="h-3 w-3" />
            </a>
            <SwitchRefPopover
              plugin={plugin}
              open={refPopoverOpen}
              onOpenChange={setRefPopoverOpen}
            >
              <button
                type="button"
                onClick={() => setRefPopoverOpen(true)}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 font-mono hover:bg-accent transition-colors"
              >
                {plugin.source.ref}
                <span className="opacity-70 text-[10px]">({plugin.source.refType})</span>
              </button>
            </SwitchRefPopover>
            <CommitShaChip sha={plugin.source.commitSha} />
            <span>Installed {installedAtLabel}</span>
            {lastSyncedLabel && <span>· Synced {lastSyncedLabel}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div
            className="flex items-center gap-2"
            title={
              isSha
                ? 'Pinned to a commit — switch to a branch or tag to enable auto-update.'
                : 'Applies on the next scheduled check, not immediately.'
            }
          >
            <span className="text-xs text-muted-foreground">Auto-update</span>
            <Switch
              checked={!!plugin.autoUpdate}
              disabled={isSha || setAutoUpdate.isPending}
              onCheckedChange={handleAutoUpdateChange}
              aria-label="Toggle auto-update"
            />
          </div>
          <Button
            size="sm"
            variant={updateAction.variant}
            onClick={updateAction.onClick}
            disabled={updateAction.disabled}
            title={updateAction.title}
          >
            {updateAction.icon === 'spinner' ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            )}
            {updateAction.label}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setUninstallOpen(true)}
            className="text-status-error-fg hover:bg-status-error-bg/40"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Uninstall
          </Button>
        </div>
      </div>
      </div>

      {/* Status banner */}
      {isSyncing && (
        <div className="flex items-center gap-3 rounded-md border border-status-info-bg bg-status-info-bg/30 px-4 py-3 text-sm">
          <Loader2 className="h-4 w-4 animate-spin text-status-info-fg" />
          <p className="text-foreground">
            {completion.stalled
              ? 'Still working — check back later.'
              : 'Syncing — this may take a few minutes.'}
          </p>
        </div>
      )}
      {plugin.status === 'failed' && plugin.failureReason && (
        <div className="rounded-md border border-status-error-bg bg-status-error-bg/40 px-4 py-3 text-sm">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 text-status-error-fg" />
            <div className="flex-1">
              <p className="font-medium text-foreground">Last operation failed</p>
              <p className="mt-1 text-muted-foreground">{plugin.failureReason}</p>
              {!isSha && (
                <Button variant="outline" size="sm" className="mt-2" onClick={handleSync}>
                  Retry sync
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <ControlledTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab}>
        {activeTab === 'resources' && (
          <div className="space-y-6">
            <ResourceGroup
              title={`Agents (${agents.length})`}
              kind="agent"
              items={agents.map((a) => ({
                key: a.agentId,
                name: a.name,
                description: a.description,
                resourceKey: a.resourceKey,
                href: routes.console.spaceAgent(spaceId, a.agentId),
              }))}
              emptyText="No agents installed by this plugin."
            />
            <ResourceGroup
              title={`Skills (${skills.length})`}
              kind="skill"
              items={skills.map((s) => ({
                key: s.skillId,
                name: s.name,
                description: s.description,
                resourceKey: s.resourceKey,
                href: routes.console.spaceSkill(spaceId, s.skillId),
              }))}
              emptyText="No skills installed by this plugin."
            />
          </div>
        )}

        {activeTab === 'skipped' && (
          <Table
            columns={[
              {
                key: 'path',
                header: 'Path',
                render: (s: Plugin['skipped'][number]) => (
                  <span className="font-mono text-xs text-foreground">{s.path}</span>
                ),
              },
              {
                key: 'type',
                header: 'Type',
                className: 'whitespace-nowrap',
                render: (s: Plugin['skipped'][number]) => (
                  <Badge variant="gray">{s.resourceType}</Badge>
                ),
              },
              {
                key: 'reason',
                header: 'Reason',
                render: (s: Plugin['skipped'][number]) => (
                  <span className="text-muted-foreground text-xs">
                    {normalizeSkipReason(s.reason)}
                  </span>
                ),
              },
            ]}
            data={plugin.skipped}
            keyExtractor={(s: Plugin['skipped'][number]) => s.path}
            emptyMessage="Nothing was skipped."
          />
        )}

        {activeTab === 'mcp' && (
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
              These MCP servers were reported but not installed. Add them manually via{' '}
              <Link
                to={routes.console.spaceToolsMcpServers(spaceId)}
                className="text-foreground underline hover:no-underline"
              >
                Tools → MCP servers
              </Link>
              .
            </div>
            <Table
              columns={[
                {
                  key: 'name',
                  header: 'Name',
                  render: (s: typeof mcpServers[number]) => (
                    <span className="font-medium text-foreground">{s.name}</span>
                  ),
                },
                {
                  key: 'command',
                  header: 'Command',
                  render: (s: typeof mcpServers[number]) => (
                    <span className="font-mono text-xs text-muted-foreground">
                      {s.command || '—'}
                    </span>
                  ),
                },
                {
                  key: 'transport',
                  header: 'Transport',
                  className: 'whitespace-nowrap',
                  render: (s: typeof mcpServers[number]) =>
                    s.transport ? <Badge variant="gray">{s.transport}</Badge> : '—',
                },
              ]}
              data={mcpServers}
              keyExtractor={(s: typeof mcpServers[number]) => s.name}
              emptyMessage="No MCP servers reported."
            />
          </div>
        )}

        {activeTab === 'source' && (
          <dl className="grid gap-4 sm:grid-cols-2 max-w-3xl">
            <SourceField label="Repository">
              <a
                href={plugin.source.repoUrl}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-sm text-foreground hover:underline"
              >
                {plugin.source.repoUrl}
              </a>
            </SourceField>
            <SourceField label="Host">
              <span className="text-sm text-foreground capitalize">{plugin.source.host}</span>
            </SourceField>
            <SourceField label="Visibility">
              <Badge variant={plugin.source.visibility === 'public' ? 'gray' : 'info'}>
                {plugin.source.visibility}
              </Badge>
            </SourceField>
            <SourceField label="Subdirectory">
              <span className="font-mono text-sm text-foreground">
                {plugin.source.subdir || '—'}
              </span>
            </SourceField>
            <SourceField label="Ref">
              <span className="font-mono text-sm text-foreground">
                {plugin.source.ref}{' '}
                <span className="text-muted-foreground text-xs">
                  ({plugin.source.refType})
                </span>
              </span>
            </SourceField>
            <SourceField label="Commit">
              <span className="font-mono text-sm text-foreground">
                {plugin.source.commitSha.slice(0, 12)}
              </span>
            </SourceField>
            <SourceField label="Connected account">
              {plugin.source.connectionId ? (
                matchedCredential ? (
                  <Link
                    to={routes.console.credentials}
                    className="inline-flex items-center gap-2 text-sm text-foreground hover:underline"
                  >
                    <span className="capitalize">{matchedCredential.provider}</span>
                    <span className="text-muted-foreground">
                      · {matchedCredential.metadata.accountName ||
                        matchedCredential.metadata.displayName ||
                        matchedCredential.metadata.externalUsername ||
                        '(unnamed)'}
                    </span>
                  </Link>
                ) : (
                  <span className="text-muted-foreground text-sm font-mono">
                    {plugin.source.connectionId}
                  </span>
                )
              ) : (
                <span className="text-muted-foreground text-sm">— (public repo)</span>
              )}
            </SourceField>
          </dl>
        )}
      </ControlledTabs>

      <ConfirmModal
        isOpen={uninstallOpen}
        onClose={() => setUninstallOpen(false)}
        onConfirm={handleUninstall}
        title="Uninstall plugin"
        message={`This will delete ${agents.length} agent${agents.length === 1 ? '' : 's'} and ${skills.length} skill${skills.length === 1 ? '' : 's'} installed by this plugin. This action cannot be undone.`}
        confirmText="Uninstall"
        isLoading={uninstall.isPending}
      />
    </div>
  );
}

function ResourceGroup({
  title,
  kind,
  items,
  emptyText,
}: {
  title: string;
  kind: ResourceKind;
  items: Array<{
    key: string;
    name: string;
    description?: string;
    resourceKey?: string;
    href: string;
  }>;
  emptyText: string;
}) {
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-medium text-foreground">{title}</h3>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((item) => (
            <ResourceCard
              key={item.key}
              icon={<ResourceIcon kind={kind} />}
              title={item.name}
              description={item.description}
              resourceKey={item.resourceKey}
              href={item.href}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function SourceField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  );
}

function CommitShaChip({ sha }: { sha: string }) {
  const [copied, setCopied] = useState(false);
  if (!sha) return null;
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(sha).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 font-mono hover:bg-accent transition-colors"
      title="Click to copy full SHA"
    >
      <Copy className="h-3 w-3" />
      {copied ? 'copied' : sha.slice(0, 7)}
    </button>
  );
}

/**
 * Backend-written skip reasons sometimes include time-bound qualifiers
 * (e.g. "Hooks not supported in phase 1") that we don't want to surface
 * verbatim. Strip a few known phrasings client-side so the UI reads
 * cleanly while the backend catches up. A no-op once those strings are
 * cleaned upstream.
 */
function normalizeSkipReason(reason: string): string {
  const stripped = reason
    .replace(/\s+in\s+phase\s+\d+/gi, '')
    .replace(/\s+in\s+this\s+release/gi, '')
    .replace(/\s+\(phase\s+\d+\)/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  // Promote "{Noun} not supported" to "{Noun} are not supported" so the
  // sentence reads cleanly after stripping the phase qualifier. Only
  // matches when the noun has no auxiliary verb in front of "not".
  return stripped.replace(
    /^(\w+)\s+not\s+supported\b/i,
    '$1 are not supported',
  );
}
